import { UserRole } from "model/user/userRole";
import React from "react";
import {
    AutocompleteInput,
    BooleanInput,
    Button, choices,
    Datagrid,
    DateField,
    DateInput,
    DeleteButton,
    EditButton,
    FormDataConsumer,
    FormTab,
    ImageField,
    ImageInput,
    Link,
    NumberField,
    Pagination,
    RadioButtonGroupInput,
    ReferenceInput,
    ReferenceManyField,
    required,
    SelectInput,
    TabbedForm,
    TextField,
    TextInput,
    usePermissions,
    useRecordContext,
} from "react-admin";
import { Box, Typography, } from '@material-ui/core';
import ChatBubbleIcon from "@material-ui/icons/ChatBubble";
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import { AdminResource } from "../resource";
import { Gender } from "model/user/gender";
import { Discipline } from "model/result/discipline";
import { ListActionToolbar } from "components/listActionToolbar";
import { TabbedFormProps } from "ra-ui-materialui/lib/form/TabbedForm";
import { useCountries, useLanguages, useTimezones } from "api/enums";
import { Capability } from "reactAdmin/capabilities";
import { useTranslation } from "react-i18next";
import { EditToolbar } from "components/editToolbar";

interface AddResultButtonProps {
    readonly discipline: Discipline,
    readonly profile: boolean,
}

const AddResultButton = ({ discipline, profile } : AddResultButtonProps) => {
    const record = useRecordContext();
    const { t } = useTranslation();

    return (
        <Button
            variant="contained"
            color="primary"
            style={{color: 'white'}}
            component={Link}
            to={{
                pathname: "/results/create",
                state: {
                    profile,
                    record: { userId: record.id, discipline }
                },
            }}
            label={t('userEdit.addResult')}
        >
            <ChatBubbleIcon />
        </Button>
    );
}

const CustomContent = ({ children } : {children: JSX.Element}) => (
    <>
        {children}
    </>
);

interface UserEditFormProps extends Omit<TabbedFormProps, 'children'> {
    readonly myProfile?: boolean,
}

export const UserEditForm = (props: UserEditFormProps) => {
    const { t } = useTranslation();
    const timezonesResult = useTimezones();
    const languagesResult = useLanguages();
    const countryResult = useCountries();
    const { loading, permissions: isAuthorized } = usePermissions();

    const formValidator = (values: any) => {
        const errors: Record<string, string> = {};

        if (values.role === UserRole.clubAdmin) {
            if (!values.organizationId) {
                errors.organizationId = 'ra.validation.required'
            }
        }

        return errors;
    }

    if (loading) {
        return null
    }

    return (
        <TabbedForm {...props} validate={formValidator} toolbar={<EditToolbar showDelete={isAuthorized && isAuthorized(Capability.DELETE_USER)} />}>
            <FormTab label={t("userEdit.tabGeneralInformation")}>
                <CustomContent>
                    <Box display="flex">
                        <Box flex={2}>
                            <ImageInput
                                labelSingle={t("Click to upload picture (2MB)")}
                                source="avatar"
                                accept="image/png, image/jpg, image/jpeg"
                                maxSize={2000000}
                            >
                                <ImageField source="src" />
                            </ImageInput>

                            <Box display="flex">
                                <Box flex={1} mr={1}>
                                    <TextInput source="firstName" validate={[required()]} fullWidth />
                                </Box>
                                <Box flex={1} ml={1}>
                                    <TextInput source="lastName" validate={[required()]} fullWidth />
                                </Box>
                            </Box>
                            <RadioButtonGroupInput
                                source="gender"
                                choices={Object.values(Gender).map((item) => ({
                                    id: item,
                                    name: item,
                                }))}
                                validate={[required()]}
                            />
                            <Box display="flex">
                                <Box flex={1} mr={1}>
                                    <DateInput source="dateOfBirth" validate={[required()]} fullWidth />
                                </Box>
                                <Box flex={1} ml={1}>
                                    <AutocompleteInput
                                        source="countryId"
                                        choices={(countryResult.data || []).map((item) => ({
                                            id: item.id,
                                            name: item.name,
                                        }))}
                                        validate={[required()]}
                                        fullWidth
                                    />
                                </Box>
                            </Box>

                            <FormDataConsumer>
                                {({ formData, ...rest }) => {
                                    const filters: Record<string, string|number|boolean> = {
                                        isActive: true
                                    };

                                    const shouldSeeAllOrganizations = props.myProfile || isAuthorized(Capability.ASSIGN_ANY_CLUB) || !props.record.organizationId;
                                    if (!shouldSeeAllOrganizations) {
                                        filters.id = props.record.organizationId
                                    }

                                    return (
                                        <ReferenceInput
                                            label={t('resource.organization')}
                                            source="organizationId"
                                            reference={AdminResource.organizations}
                                            allowEmpty={formData.role !== UserRole.clubAdmin}
                                            isRequired={formData.role === UserRole.clubAdmin}
                                            disabled={props.myProfile && formData.role === UserRole.clubAdmin}
                                            filter={filters}
                                        >
                                            <SelectInput
                                                optionText="name"
                                                fullWidth
                                            />
                                        </ReferenceInput>
                                    )
                                }}
                            </FormDataConsumer>
                            <Box display="flex">
                                <Box flex={1} mr={1}>
                                    <TextInput source="emailAddress" disabled fullWidth />
                                </Box>
                                <Box flex={1} ml={2}>
                                    {props.record.phoneNumber && <TextInput source="phoneNumber" disabled fullWidth />}
                                </Box>
                            </Box>
                            {props.myProfile && <BooleanInput label={t('signUp.isMarketingConsent')} source="isMarketingConsent" validate={[
                                required(t('signUp.acceptGdpr')),
                                choices([true], t('signUp.acceptGdpr')),
                            ]} />}
                        </Box>
                        <Box flex={1} ml={6}>
                            <Typography variant="h6" gutterBottom>
                                {t('userEdit.userSettings')}
                            </Typography>

                            <AutocompleteInput
                                source="role"
                                choices={Object.values(UserRole)
                                    .filter(item => isAuthorized(Capability.ASSIGN_ROLE_ADMIN) ? true : item !== UserRole.administrator)
                                    .map((item) => ({
                                        id: item,
                                        name: item,
                                }))}
                                validate={[required()]}
                                disabled={props.myProfile}
                                fullWidth
                            />
                            {!props.myProfile && isAuthorized(Capability.ENABLE_USER) && isAuthorized(Capability.DISABLE_USER) && <BooleanInput source="isDisabled" />}

                            <Typography variant="h6" gutterBottom>
                                {t('userEdit.systemSettings')}
                            </Typography>

                            <AutocompleteInput
                                source="languageId"
                                choices={(languagesResult.data || []).map((item) => ({
                                    id: item.id,
                                    name: item.name,
                                }))}
                                validate={[required()]}
                                fullWidth
                            />

                            <AutocompleteInput
                                source="timeZone"
                                choices={(timezonesResult.data || []).map((item) => ({
                                    id: item.name,
                                    name: item.name,
                                }))}
                                validate={[required()]}
                                fullWidth
                            />

                            {!props.myProfile && (
                                <>
                                    <Box flexGrow={1}>
                                        <Box display="flex" mb="1em">
                                            <Box mr="1em">
                                                <AccessTimeIcon
                                                    fontSize="small"
                                                    color="disabled"
                                                />
                                            </Box>
                                            <Box flexGrow={1}>
                                                <Typography>{t('userEdit.activatedAt')}</Typography>
                                                <DateField source="activatedAt" showTime/>
                                            </Box>
                                        </Box>
                                    </Box>

                                    <Box flexGrow={1}>
                                        <Box display="flex" mb="1em">
                                            <Box mr="1em">
                                                <AccessTimeIcon
                                                    fontSize="small"
                                                    color="disabled"
                                                />
                                            </Box>
                                            <Box flexGrow={1}>
                                                <Typography>{t('userEdit.signInAt')}</Typography>
                                                <DateField source="signedInAt" showTime/>
                                            </Box>
                                        </Box>
                                    </Box>
                                </>
                            )}
                        </Box>
                    </Box>
                </CustomContent>
            </FormTab>
            <FormTab label={t("userEdit.tabShooterInformation")} path="shooter">
                <CustomContent>
                    <Box>
                        <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="h4" gutterBottom>
                                {t('discipline.10mAirPistol')}
                            </Typography>
                            <Box textAlign="right">
                                <AddResultButton discipline={Discipline.airPistol10m} profile={props.myProfile === true} />
                            </Box>
                        </Box>
                        <ReferenceManyField
                            reference={AdminResource.results}
                            target="userId"
                            filter={{discipline: Discipline.airPistol10m}}
                            pagination={<Pagination />}
                        >
                            <Datagrid>
                                <TextField source="id"/>
                                <DateField source="date"/>
                                <TextField source="championshipName"/>
                                <NumberField source="totalScore"/>
                                <NumberField source="rank"/>
                                <ListActionToolbar>
                                    <EditButton/>
                                    <DeleteButton redirect={false}/>
                                </ListActionToolbar>
                            </Datagrid>
                        </ReferenceManyField>

                        <Box mt={4} display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="h4" gutterBottom>
                                {t('discipline.10mAirRifle')}
                            </Typography>
                            <Box textAlign="right">
                                <AddResultButton discipline={Discipline.airRifle10m} profile={props.myProfile === true} />
                            </Box>
                        </Box>
                        <ReferenceManyField
                            reference={AdminResource.results}
                            target="userId"
                            filter={{discipline: Discipline.airRifle10m}}
                            pagination={<Pagination />}
                        >
                            <Datagrid>
                                <TextField source="id"/>
                                <DateField source="date"/>
                                <TextField source="championshipName"/>
                                <NumberField source="totalScore"/>
                                <NumberField source="rank"/>
                                <ListActionToolbar>
                                    <EditButton/>
                                    <DeleteButton redirect={false}/>
                                </ListActionToolbar>
                            </Datagrid>
                        </ReferenceManyField>
                    </Box>
                </CustomContent>
            </FormTab>
        </TabbedForm>
    );
};
